/**
 * maps_intelligent_store.ts — Store Zustand del flujo "Mapas
 * inteligentes" (Hito 1 MapeoData).
 *
 * Gestiona el estado de la página `pages/Search.tsx`:
 *   - **Modo de selección de zona** (`zone_mode`):
 *     - `'admin'` → selector jerárquico (país → región → provincia → municipio).
 *     - `'free_circle'` → coordenadas + radio libre (decisión A
 *       heredada T-BLS-UI: lat/lng numéricos + slider 5-100 km step 5).
 *   - **Sub-estado modo admin** (`zone_admin`): país + nivel + query
 *     parcial + zona seleccionada.
 *   - **Sub-estado modo círculo** (`zone_circle`): centro + radio.
 *   - **Decisión G heredada T-BLS-UI**: el toggle entre modos preserva
 *     ambos sub-estados independientemente (no se resetean al cambiar
 *     `zone_mode`).
 *
 * **Filosofía Zustand canónica** (paridad con `store/session.ts`):
 *   - State plano + setters explícitos (no actions reducers).
 *   - Setters parciales (`set_zone_admin(partial)`) hacen `{...prev,
 *     ...partial}` automáticamente.
 *   - Sin `persist` middleware en este store (las selecciones son
 *     efímeras por sesión de búsqueda; el JWT sí se persiste en
 *     `store/session.ts`).
 *
 * @module store/maps_intelligent_store
 */

import { create } from 'zustand'
import type { AdminLevel, AdminZoneSearchResult } from '@/services/listingsService'

// ============================================================================
// TIPOS
// ============================================================================

export type ZoneMode = 'admin' | 'free_circle'

export interface ZoneAdminState {
  /** ISO 3166-1 alpha-2 del país seleccionado. Vacío = sin selección. */
  country_code: string
  /** Nivel admin que el usuario quiere apuntar. Null = aún no decidido. */
  level: AdminLevel | null
  /** Texto del input de búsqueda parcial (decisión M: persistencia query
   *  parcial para que el EstimatorCard pueda mostrar fallback label
   *  cuando aún no hay `selected_zone`). */
  query_text: string
  /** Zona admin finalmente seleccionada (la que se enviará al estimate/
   *  search). Null hasta confirmación del usuario. */
  selected_zone: AdminZoneSearchResult | null
}

export interface ZoneCircleState {
  /** Latitud del centro (rango [-90, 90]). */
  center_lat: number
  /** Longitud del centro (rango [-180, 180]). */
  center_lng: number
  /** Radio en kilómetros (rango [5, 100] step 5, decisión A T-BLS-UI). */
  radius_km: number
}

interface MapsIntelligentStore {
  // ---- Estado ----
  zone_mode: ZoneMode
  zone_admin: ZoneAdminState
  zone_circle: ZoneCircleState

  // ---- Setters ----
  set_zone_mode: (mode: ZoneMode) => void
  /** Partial update del sub-estado admin. */
  set_zone_admin: (partial: Partial<ZoneAdminState>) => void
  /** Partial update del sub-estado círculo. */
  set_zone_circle: (partial: Partial<ZoneCircleState>) => void
  /** Reset completo (útil entre searches o al cerrar sesión). */
  reset: () => void
}

// ============================================================================
// DEFAULTS
// ============================================================================

/**
 * Defaults del modo círculo libre. España continental como centro
 * geográfico inicial razonable (España es el mercado foco T-001
 * coverage_tier=1 según T-BLS heredado). Radio 25 km es punto medio
 * útil para zona urbana ampliada.
 */
const DEFAULT_ZONE_CIRCLE: ZoneCircleState = {
  center_lat: 40.4168,  // Madrid lat
  center_lng: -3.7038,  // Madrid lng
  radius_km: 25,
}

const DEFAULT_ZONE_ADMIN: ZoneAdminState = {
  country_code: '',
  level: null,
  query_text: '',
  selected_zone: null,
}

// ============================================================================
// STORE
// ============================================================================

export const useMapsIntelligentStore = create<MapsIntelligentStore>(set => ({
  zone_mode: 'admin',
  zone_admin: DEFAULT_ZONE_ADMIN,
  zone_circle: DEFAULT_ZONE_CIRCLE,

  set_zone_mode: mode => set({ zone_mode: mode }),

  set_zone_admin: partial =>
    set(state => ({
      zone_admin: { ...state.zone_admin, ...partial },
    })),

  set_zone_circle: partial =>
    set(state => ({
      zone_circle: { ...state.zone_circle, ...partial },
    })),

  reset: () =>
    set({
      zone_mode: 'admin',
      zone_admin: DEFAULT_ZONE_ADMIN,
      zone_circle: DEFAULT_ZONE_CIRCLE,
    }),
}))
